import { useCallback, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FeatureFlag } from '../constants/featureFlags';

// Flags with explicit default values when not defined in LaunchDarkly
const EXPLICIT_DEFAULTS: Partial<Record<FeatureFlag, boolean>> = {
  [FeatureFlag.REGISTRATION]: true,
};

/**
 * Provides functionality to handle feature flags using LaunchDarkly's React SDK.
 *
 * This hook allows you to check the status of feature flags and determine if they are enabled.
 * It also provides access to all available flags and a utility to check if flags are loaded.
 *
 * Dependencies:
 * - `launchdarkly-react-client-sdk`: Used to fetch and manage feature flags.
 * - React hooks: `useCallback` and `useMemo` for memoization and performance optimization.
 *
 * @returns {Object} An object containing:
 * - `flags`: An object representing all feature flags and their current states.
 * - `flagsAvailable`: A boolean indicating if any flags are available.
 * - `isFeatureEnabled`: A function to check if a specific feature flag or a group of flags is enabled.
 *
 * @function isFeatureEnabled
 * @param {FeatureFlag | FeatureFlag[] | null} flag
 * A single feature flag, an array of feature flags, or `null`.
 *
 * When `null` is provided, the function returns `undefined`.
 *
 * @param {'every' | 'some'} [featureFlagCheckMode='every']
 * Defines how multiple feature flags should be evaluated when `flag` is an array.
 *
 * - `every`: returns `true` only if all provided feature flags are enabled.
 * - `some`: returns `true` if at least one of the provided feature flags is enabled.
 *
 * This parameter is ignored when `flag` is a single feature flag.
 *
 * @returns {boolean | undefined}
 * Returns:
 * - `true` if the requested feature flag condition is satisfied.
 * - `false` if the requested feature flag condition is not satisfied.
 * - `undefined` if `flag` is `null`.
 *
 * For single feature flags, explicit defaults from `EXPLICIT_DEFAULTS` are used when the flag
 * is not defined in LaunchDarkly. If no explicit default exists, the function returns `false`.
 *
 * @example
 * import { useFeatureFlags } from './hooks/useFeatureFlags';
 * import { FeatureFlag } from './constants/featureFlags';
 *
 * const MyComponent = () => {
 *   const { isFeatureEnabled, flagsAvailable } = useFeatureFlags();
 *
 *   if (!flagsAvailable) {
 *     return <div>Loading feature flags...</div>;
 *   }
 *
 *   const isNewFeatureEnabled = isFeatureEnabled(FeatureFlag.NewFeature);
 *
 *   const areAllFeaturesEnabled = isFeatureEnabled([
 *     FeatureFlag.FeatureA,
 *     FeatureFlag.FeatureB,
 *   ]);
 *
 *   const isAtLeastOneFeatureEnabled = isFeatureEnabled(
 *     [FeatureFlag.FeatureA, FeatureFlag.FeatureB],
 *     'some',
 *   );
 *
 *   return (
 *     <div>
 *       {isNewFeatureEnabled ? <p>New Feature is enabled!</p> : <p>New Feature is disabled.</p>}
 *
 *       {areAllFeaturesEnabled ? (
 *         <p>All selected features are enabled!</p>
 *       ) : (
 *         <p>At least one selected feature is disabled.</p>
 *       )}
 *
 *       {isAtLeastOneFeatureEnabled ? (
 *         <p>At least one selected feature is enabled!</p>
 *       ) : (
 *         <p>No selected features are enabled.</p>
 *       )}
 *     </div>
 *   );
 * };
 */
export const useFeatureFlags = () => {
  const flags = useFlags();

  const isFeatureEnabled = useCallback(
    (flag: FeatureFlag | FeatureFlag[] | null, featureFlagCheckMode?: 'every' | 'some') => {
      if (flag === null) {
        return undefined;
      }
      if (Array.isArray(flag)) {
        const allFlagsEnabled =
          featureFlagCheckMode === 'some'
            ? flag.some(f => !!flags[f])
            : flag.every(f => !!flags[f]);
        return allFlagsEnabled;
      }
      if (flag === null) {
        return true;
      }
      const flagValue = flags[flag];
      return flagValue !== undefined ? !!flagValue : (EXPLICIT_DEFAULTS[flag] ?? false);
    },
    [flags],
  );

  const flagsAvailable = useMemo(() => flags && Object.keys(flags).length > 0, [flags]);

  return { flags, flagsAvailable, isFeatureEnabled };
};
